import { render, button, playerCard } from './components';
import { GameManager } from '../game';
import { PlayerState, MatchResult, CardInstance, BattleLogEntry } from '../types';
import { getCardById } from '../cards';

export interface SpectatorCallbacks {
  onExit: () => void;
  onSelectMatch?: (matchId: string) => void;
}

// 観戦モード状態
interface SpectatorState {
  selectedMatchIndex: number;
  autoRefresh: boolean;
  refreshInterval: number | null;
  viewMode: 'ranking' | 'matches' | 'player';
  selectedPlayerId: string | null;
}

const spectatorState: SpectatorState = {
  selectedMatchIndex: 0,
  autoRefresh: true,
  refreshInterval: null,
  viewMode: 'ranking',
  selectedPlayerId: null,
};

// 観戦モードスタイル注入
function injectSpectatorStyles(): void {
  if (document.getElementById('spectator-styles')) return;

  const style = document.createElement('style');
  style.id = 'spectator-styles';
  style.textContent = `
    .spectator-container {
      min-height: 100vh;
      min-height: 100dvh;
      background: linear-gradient(180deg, #0a0a1a 0%, #1a1a2e 50%, #0f0f23 100%);
      padding: 12px;
    }
    
    .spectator-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 16px;
      background: rgba(0,0,0,0.4);
      border-radius: 12px;
      margin-bottom: 16px;
      border: 1px solid rgba(0, 212, 255, 0.2);
    }
    
    .spectator-badge {
      display: flex;
      align-items: center;
      gap: 8px;
      color: #00d4ff;
      font-weight: bold;
    }
    
    .spectator-badge .live-dot {
      width: 8px;
      height: 8px;
      background: #ff4757;
      border-radius: 50%;
      animation: livePulse 1.5s ease-in-out infinite;
    }
    
    @keyframes livePulse {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.5; transform: scale(1.2); }
    }
    
    .spectator-tabs {
      display: flex;
      gap: 8px;
      margin-bottom: 16px;
      overflow-x: auto;
      padding-bottom: 4px;
    }
    
    .spectator-tab {
      background: rgba(255,255,255,0.05);
      border: 1px solid rgba(255,255,255,0.1);
      color: #888;
      padding: 10px 16px;
      border-radius: 8px;
      cursor: pointer;
      font-size: 0.85rem;
      white-space: nowrap;
      transition: all 0.2s;
    }
    
    .spectator-tab.active {
      background: rgba(0, 212, 255, 0.2);
      border-color: #00d4ff;
      color: #00d4ff;
    }
    
    .spectator-tab:hover:not(.active) {
      background: rgba(255,255,255,0.1);
      color: #fff;
    }
    
    .match-card {
      background: rgba(0,0,0,0.3);
      border: 1px solid rgba(255,255,255,0.1);
      border-radius: 12px;
      padding: 16px;
      margin-bottom: 12px;
      cursor: pointer;
      transition: all 0.2s;
    }
    
    .match-card:hover {
      border-color: #00d4ff;
      transform: translateY(-2px);
    }
    
    .match-card.selected {
      border-color: #ffd700;
      box-shadow: 0 0 20px rgba(255, 215, 0, 0.2);
    }
    
    .match-vs {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
    }
    
    .match-player {
      flex: 1;
      text-align: center;
    }
    
    .match-player.winner {
      color: #2ecc71;
    }
    
    .match-player.loser {
      color: #888;
      opacity: 0.7;
    }
    
    .vs-divider {
      color: #ffd700;
      font-weight: bold;
      font-size: 0.9rem;
    }
    
    .player-detail-card {
      background: rgba(0,0,0,0.3);
      border-radius: 12px;
      padding: 20px;
      margin-bottom: 16px;
    }
    
    .deck-preview {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin-top: 12px;
    }
    
    .mini-card {
      background: rgba(255,255,255,0.1);
      border: 1px solid rgba(255,255,255,0.2);
      border-radius: 6px;
      padding: 4px 8px;
      font-size: 0.7rem;
      display: flex;
      align-items: center;
      gap: 4px;
    }
    
    .match-replay {
      background: rgba(0,0,0,0.4);
      border-radius: 12px;
      padding: 16px;
      margin-top: 16px;
    }
    
    .replay-step {
      display: flex;
      align-items: center;
      padding: 8px 12px;
      margin: 4px 0;
      background: rgba(255,255,255,0.03);
      border-radius: 6px;
      font-size: 0.8rem;
    }
    
    .replay-step.flag-change {
      background: rgba(255, 215, 0, 0.1);
      border-left: 3px solid #ffd700;
    }
    
    .stat-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
      gap: 12px;
      margin-top: 12px;
    }
    
    .stat-item {
      background: rgba(0,0,0,0.2);
      border-radius: 8px;
      padding: 12px;
      text-align: center;
    }
    
    .stat-value {
      font-size: 1.5rem;
      font-weight: bold;
      color: #fff;
    }
    
    .stat-label {
      font-size: 0.7rem;
      color: #666;
      margin-top: 4px;
    }
  `;
  document.head.appendChild(style);
}

// メイン観戦画面
export function renderSpectatorMode(
  game: GameManager,
  callbacks: SpectatorCallbacks
): void {
  injectSpectatorStyles();
  
  const gameState = game.getState();
  const ranking = game.getRanking();
  const currentRound = gameState.currentRound?.roundNumber ?? gameState.roundHistory.length;
  const phase = gameState.phase;
  const activeCount = ranking.filter((r) => !r.player.isEliminated).length;

  // 自動更新の設定
  if (spectatorState.autoRefresh && spectatorState.refreshInterval === null) {
    spectatorState.refreshInterval = window.setInterval(() => {
      renderSpectatorMode(game, callbacks);
    }, 3000);
  }

  render(`
    <div class="spectator-container">
      <!-- ヘッダー -->
      <div class="spectator-header">
        <div class="spectator-badge">
          <div class="live-dot"></div>
          <span>👁️ SPECTATOR MODE</span>
        </div>
        <div style="display: flex; align-items: center; gap: 12px;">
          <span style="color: #888; font-size: 0.85rem;">Round ${currentRound}</span>
          <span style="color: #666;">|</span>
          <span style="color: #aaa; font-size: 0.85rem;">${activeCount}人生存</span>
          <button id="btnExitSpectator" style="
            background: transparent;
            border: 1px solid #666;
            color: #888;
            padding: 6px 14px;
            border-radius: 6px;
            font-size: 0.8rem;
            cursor: pointer;
          ">退出</button>
        </div>
      </div>

      <!-- フェーズ表示 -->
      <div style="
        text-align: center;
        padding: 12px;
        background: ${getPhaseColor(phase)}20;
        border: 1px solid ${getPhaseColor(phase)};
        border-radius: 8px;
        margin-bottom: 16px;
      ">
        <span style="color: ${getPhaseColor(phase)}; font-weight: bold;">
          ${getPhaseIcon(phase)} ${getPhaseText(phase)}
        </span>
      </div>

      <!-- タブ -->
      <div class="spectator-tabs">
        <div class="spectator-tab ${spectatorState.viewMode === 'ranking' ? 'active' : ''}" data-mode="ranking">
          📊 ランキング
        </div>
        <div class="spectator-tab ${spectatorState.viewMode === 'matches' ? 'active' : ''}" data-mode="matches">
          ⚔️ 対戦結果
        </div>
        <div class="spectator-tab ${spectatorState.viewMode === 'player' ? 'active' : ''}" data-mode="player">
          👤 プレイヤー詳細
        </div>
      </div>

      <!-- コンテンツ -->
      <div id="spectatorContent">
        ${renderSpectatorContent(game, spectatorState.viewMode)}
      </div>
    </div>
  `);

  // イベント
  document.getElementById('btnExitSpectator')?.addEventListener('click', () => {
    stopAutoRefresh();
    callbacks.onExit();
  });

  document.querySelectorAll('.spectator-tab').forEach((tab) => {
    tab.addEventListener('click', () => {
      const mode = tab.getAttribute('data-mode') as 'ranking' | 'matches' | 'player';
      spectatorState.viewMode = mode;
      renderSpectatorMode(game, callbacks);
    });
  });

  // プレイヤー選択イベント
  document.querySelectorAll('.player-row').forEach((row) => {
    row.addEventListener('click', () => {
      const playerId = row.getAttribute('data-player');
      if (playerId) {
        spectatorState.selectedPlayerId = playerId;
        spectatorState.viewMode = 'player';
        renderSpectatorMode(game, callbacks);
      }
    });
  });

  // マッチ選択イベント
  document.querySelectorAll('.match-card').forEach((card) => {
    card.addEventListener('click', () => {
      const idx = parseInt(card.getAttribute('data-match-idx') ?? '0');
      spectatorState.selectedMatchIndex = idx;
      renderSpectatorMode(game, callbacks);
    });
  });
}

// コンテンツ描画
function renderSpectatorContent(game: GameManager, mode: string): string {
  switch (mode) {
    case 'ranking':
      return renderRankingView(game);
    case 'matches':
      return renderMatchesView(game);
    case 'player':
      return renderPlayerDetailView(game);
    default:
      return renderRankingView(game);
  }
}

// ランキングビュー
function renderRankingView(game: GameManager): string {
  const ranking = game.getRanking();

  return `
    <div style="
      background: rgba(0,0,0,0.3);
      border-radius: 12px;
      padding: 16px;
    ">
      <h3 style="color: #ffd700; margin-bottom: 16px; font-size: 1rem;">
        🏆 現在のランキング
      </h3>
      
      ${ranking.map((r, i) => {
        const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : '';
        const isFinalist = i < 2 && !r.player.isEliminated;
        const isEliminated = r.player.isEliminated;
        const wins = r.player.matchHistory.filter(m => m.result === 'win').length;
        const losses = r.player.matchHistory.filter(m => m.result === 'lose').length;
        const streak = calculateStreak(r.player.matchHistory);
        
        return `
          <div class="player-row" data-player="${r.player.info.id}" style="
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 12px 14px;
            margin: 6px 0;
            background: ${isFinalist ? 'rgba(255,215,0,0.1)' : 'rgba(255,255,255,0.03)'};
            border-radius: 8px;
            cursor: pointer;
            transition: all 0.2s;
            ${isFinalist ? 'border: 1px solid rgba(255,215,0,0.3);' : 'border: 1px solid transparent;'}
            ${isEliminated ? 'opacity: 0.4;' : ''}
          ">
            <div style="display: flex; align-items: center; gap: 12px;">
              <span style="font-size: 1.2rem; width: 32px; text-align: center;">
                ${medal || `<span style="color: #666; font-size: 0.9rem;">#${r.rank}</span>`}
              </span>
              <div>
                <div style="display: flex; align-items: center; gap: 6px;">
                  <span>${r.player.info.type === 'bot' ? '🤖' : '👤'}</span>
                  <span style="font-weight: bold; font-size: 0.95rem;">${r.player.info.name}</span>
                  ${isEliminated ? '<span style="color: #e74c3c; font-size: 0.65rem; background: rgba(231,76,60,0.2); padding: 2px 6px; border-radius: 4px; margin-left: 6px;">OUT</span>' : ''}
                </div>
                <div style="font-size: 0.7rem; color: #666; margin-top: 2px;">
                  ${wins}W - ${losses}L
                  ${streak !== 0 ? `<span style="color: ${streak > 0 ? '#2ecc71' : '#e74c3c'}; margin-left: 6px;">${streak > 0 ? '🔥' : '❄️'}${Math.abs(streak)}</span>` : ''}
                </div>
              </div>
            </div>
            <div style="text-align: right;">
              <div style="color: #ffd700; font-weight: bold; font-size: 1.1rem;">★ ${r.player.fans}</div>
              <div style="font-size: 0.65rem; color: #666;">デッキ ${r.player.deck.length}枚</div>
            </div>
          </div>
        `;
      }).join('')}
    </div>
  `;
}

// 対戦結果ビュー
function renderMatchesView(game: GameManager): string {
  const gameState = game.getState();
  const allMatches: { roundNum: number; match: MatchResult }[] = [];

  // 全ラウンドのマッチを収集
  for (const round of gameState.roundHistory) {
    for (const match of round.matchResults) {
      allMatches.push({ roundNum: round.roundNumber, match });
    }
  }

  // 現在のラウンドのマッチも追加
  if (gameState.currentRound) {
    for (const match of gameState.currentRound.matchResults) {
      allMatches.push({ roundNum: gameState.currentRound.roundNumber, match });
    }
  }

  // 新しい順にソート
  allMatches.reverse();

  const selectedMatch = allMatches[spectatorState.selectedMatchIndex];

  return `
    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px;">
      <!-- マッチリスト -->
      <div style="background: rgba(0,0,0,0.3); border-radius: 12px; padding: 16px; max-height: 400px; overflow-y: auto;">
        <h3 style="color: #00d4ff; margin-bottom: 12px; font-size: 0.95rem;">
          ⚔️ 対戦履歴 (${allMatches.length}試合)
        </h3>
        
        ${allMatches.length === 0 ? '<p style="color: #666; font-size: 0.85rem;">まだ対戦がありません</p>' : ''}
        
        ${allMatches.map((m, idx) => {
          const winner = game.getPlayer(m.match.winnerId);
          const loser = game.getPlayer(m.match.loserId);
          const isSelected = idx === spectatorState.selectedMatchIndex;
          
          return `
            <div class="match-card ${isSelected ? 'selected' : ''}" data-match-idx="${idx}">
              <div style="font-size: 0.7rem; color: #666; margin-bottom: 8px;">Round ${m.roundNum}</div>
              <div class="match-vs">
                <div class="match-player winner">
                  <div style="font-size: 1.2rem;">🏆</div>
                  <div style="font-size: 0.85rem; font-weight: bold;">${winner?.info.name ?? '???'}</div>
                </div>
                <div class="vs-divider">VS</div>
                <div class="match-player loser">
                  <div style="font-size: 1.2rem;">💀</div>
                  <div style="font-size: 0.85rem;">${loser?.info.name ?? '???'}</div>
                </div>
              </div>
              <div style="text-align: center; margin-top: 8px; font-size: 0.75rem; color: #888;">
                ${m.match.finalState?.turnCount ?? 0}ターン
              </div>
            </div>
          `;
        }).join('')}
      </div>

      <!-- マッチ詳細 -->
      <div style="background: rgba(0,0,0,0.3); border-radius: 12px; padding: 16px;">
        ${selectedMatch ? renderMatchDetail(game, selectedMatch.match, selectedMatch.roundNum) : '<p style="color: #666; text-align: center;">マッチを選択してください</p>'}
      </div>
    </div>
  `;
}

// マッチ詳細
function renderMatchDetail(game: GameManager, match: MatchResult, roundNum: number): string {
  const winner = game.getPlayer(match.winnerId);
  const loser = game.getPlayer(match.loserId);
  const log: BattleLogEntry[] = match.finalState?.log ?? [];

  return `
    <h3 style="color: #ffd700; margin-bottom: 16px; font-size: 0.95rem;">
      📋 Round ${roundNum} マッチ詳細
    </h3>
    
    <div style="display: flex; justify-content: space-around; margin-bottom: 16px;">
      <div style="text-align: center;">
        <div style="color: #2ecc71; font-size: 0.75rem; margin-bottom: 4px;">Winner</div>
        <div style="font-weight: bold;">${winner?.info.name ?? '???'}</div>
        <div style="color: #ffd700; font-size: 0.85rem;">+${match.winnerFansGained} ★</div>
      </div>
      <div style="text-align: center;">
        <div style="color: #e74c3c; font-size: 0.75rem; margin-bottom: 4px;">Loser</div>
        <div style="font-weight: bold; opacity: 0.7;">${loser?.info.name ?? '???'}</div>
        <div style="color: #888; font-size: 0.85rem;">🚩 ${match.winnerFlags}</div>
      </div>
    </div>

    <div style="
      background: rgba(0,0,0,0.2);
      border-radius: 8px;
      padding: 12px;
      max-height: 200px;
      overflow-y: auto;
    ">
      <div style="color: #888; font-size: 0.75rem; margin-bottom: 8px;">バトルログ (${log.length}アクション)</div>
      ${log.slice(-10).map((entry: BattleLogEntry) => {
        const isFlagAction = entry.action === 'flagTaken';
        return `
          <div class="replay-step ${isFlagAction ? 'flag-change' : ''}">
            <span style="color: #666; width: 30px; font-size: 0.7rem;">T${entry.turn}</span>
            <span style="flex: 1; font-size: 0.8rem;">
              ${entry.message}
            </span>
            ${isFlagAction ? '<span style="color: #ffd700; font-size: 0.7rem;">🚩</span>' : ''}
          </div>
        `;
      }).join('')}
    </div>
  `;
}

// プレイヤー詳細ビュー
function renderPlayerDetailView(game: GameManager): string {
  const ranking = game.getRanking();
  
  // 選択されたプレイヤー、なければ1位を表示
  const selectedId = spectatorState.selectedPlayerId ?? ranking[0]?.player.info.id;
  const playerRank = ranking.find((r) => r.player.info.id === selectedId);
  
  if (!playerRank) {
    return '<p style="color: #666; text-align: center;">プレイヤーが見つかりません</p>';
  }

  const player = playerRank.player;
  const wins = player.matchHistory.filter(m => m.result === 'win').length;
  const losses = player.matchHistory.filter(m => m.result === 'lose').length;
  const streak = calculateStreak(player.matchHistory);

  // デッキ分析
  const deckAnalysis = analyzeDeck(player.deck);

  return `
    <!-- プレイヤー選択 -->
    <div style="margin-bottom: 16px; display: flex; gap: 8px; flex-wrap: wrap;">
      ${ranking.filter(r => !r.player.isEliminated).slice(0, 8).map((r) => `
        <button class="player-select-btn" data-player="${r.player.info.id}" style="
          background: ${r.player.info.id === selectedId ? 'rgba(0, 212, 255, 0.2)' : 'rgba(255,255,255,0.05)'};
          border: 1px solid ${r.player.info.id === selectedId ? '#00d4ff' : 'rgba(255,255,255,0.1)'};
          color: ${r.player.info.id === selectedId ? '#00d4ff' : '#888'};
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 0.8rem;
          cursor: pointer;
        ">
          ${r.player.info.type === 'bot' ? '🤖' : '👤'} ${r.player.info.name}
        </button>
      `).join('')}
    </div>

    <div class="player-detail-card">
      <!-- ヘッダー -->
      <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 16px;">
        <div style="
          width: 60px;
          height: 60px;
          background: linear-gradient(135deg, #00d4ff, #00ff88);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.8rem;
        ">
          ${player.info.type === 'bot' ? '🤖' : '👤'}
        </div>
        <div>
          <div style="font-size: 1.3rem; font-weight: bold; color: #fff;">${player.info.name}</div>
          <div style="color: #888; font-size: 0.85rem;">
            ランキング #${playerRank.rank}
            ${player.isEliminated ? '<span style="color: #e74c3c; margin-left: 8px;">敗退</span>' : ''}
          </div>
        </div>
      </div>

      <!-- スタッツ -->
      <div class="stat-grid">
        <div class="stat-item">
          <div class="stat-value" style="color: #ffd700;">★ ${player.fans}</div>
          <div class="stat-label">ファン</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">
            <span style="color: #2ecc71;">${wins}</span>
            <span style="color: #666;"> - </span>
            <span style="color: #e74c3c;">${losses}</span>
          </div>
          <div class="stat-label">勝敗</div>
        </div>
        <div class="stat-item">
          <div class="stat-value" style="color: ${streak > 0 ? '#2ecc71' : streak < 0 ? '#e74c3c' : '#888'};">
            ${streak > 0 ? '+' : ''}${streak}
          </div>
          <div class="stat-label">連勝/連敗</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">${player.deck.length}</div>
          <div class="stat-label">デッキ枚数</div>
        </div>
      </div>

      <!-- 対戦履歴 -->
      <div style="margin-top: 16px;">
        <div style="color: #888; font-size: 0.75rem; margin-bottom: 8px;">対戦履歴</div>
        <div style="display: flex; gap: 4px; flex-wrap: wrap;">
          ${player.matchHistory.map((m) => `
            <div style="
              width: 28px;
              height: 28px;
              display: flex;
              align-items: center;
              justify-content: center;
              border-radius: 4px;
              font-size: 0.75rem;
              font-weight: bold;
              background: ${m.result === 'win' ? 'rgba(46,204,113,0.2)' : 'rgba(231,76,60,0.2)'};
              color: ${m.result === 'win' ? '#2ecc71' : '#e74c3c'};
              border: 1px solid ${m.result === 'win' ? '#2ecc71' : '#e74c3c'}40;
            ">
              ${m.result === 'win' ? 'W' : 'L'}
            </div>
          `).join('')}
        </div>
      </div>
    </div>

    <!-- デッキ分析 -->
    <div style="
      background: rgba(0,0,0,0.3);
      border-radius: 12px;
      padding: 16px;
    ">
      <h4 style="color: #00d4ff; margin-bottom: 12px; font-size: 0.9rem;">📊 デッキ分析</h4>
      
      <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; margin-bottom: 12px;">
        <div style="background: rgba(0,0,0,0.2); padding: 10px; border-radius: 8px;">
          <div style="color: #888; font-size: 0.7rem;">平均パワー</div>
          <div style="color: #ffd700; font-size: 1.2rem; font-weight: bold;">⚡ ${deckAnalysis.avgPower}</div>
        </div>
        <div style="background: rgba(0,0,0,0.2); padding: 10px; border-radius: 8px;">
          <div style="color: #888; font-size: 0.7rem;">最大パワー</div>
          <div style="color: #ff6b6b; font-size: 1.2rem; font-weight: bold;">⚡ ${deckAnalysis.maxPower}</div>
        </div>
      </div>

      <!-- カテゴリ分布 -->
      <div style="margin-bottom: 12px;">
        <div style="color: #888; font-size: 0.7rem; margin-bottom: 6px;">カテゴリー分布</div>
        <div style="display: flex; flex-wrap: wrap; gap: 6px;">
          ${Object.entries(deckAnalysis.categoryCount).map(([cat, count]) => `
            <div style="
              background: ${getCategoryColor(cat)}20;
              border: 1px solid ${getCategoryColor(cat)};
              padding: 4px 10px;
              border-radius: 6px;
              font-size: 0.75rem;
              color: #fff;
            ">
              ${getCategoryIcon(cat)} ${count}
            </div>
          `).join('')}
        </div>
      </div>

      <!-- ティア分布 -->
      <div>
        <div style="color: #888; font-size: 0.7rem; margin-bottom: 6px;">ティア分布</div>
        <div style="display: flex; gap: 10px;">
          ${(['A', 'B', 'C'] as const).map((tier) => {
            const count = deckAnalysis.tierCount[tier] ?? 0;
            const total = player.deck.length;
            const pct = total > 0 ? Math.round((count / total) * 100) : 0;
            return `
              <div style="flex: 1; background: rgba(0,0,0,0.2); padding: 8px; border-radius: 6px; text-align: center;">
                <div style="color: ${getTierColor(tier)}; font-weight: bold; font-size: 0.9rem;">Tier ${tier}</div>
                <div style="color: #fff; font-size: 1rem;">${count}</div>
                <div style="
                  height: 4px;
                  background: #333;
                  border-radius: 2px;
                  margin-top: 6px;
                  overflow: hidden;
                ">
                  <div style="width: ${pct}%; height: 100%; background: ${getTierColor(tier)};"></div>
                </div>
              </div>
            `;
          }).join('')}
        </div>
      </div>
    </div>
  `;
}

// ユーティリティ関数
function calculateStreak(history: { result: 'win' | 'lose' }[]): number {
  if (history.length === 0) return 0;
  
  let streak = 0;
  const lastResult = history[history.length - 1].result;
  
  for (let i = history.length - 1; i >= 0; i--) {
    if (history[i].result === lastResult) {
      streak += lastResult === 'win' ? 1 : -1;
    } else {
      break;
    }
  }
  
  return streak;
}

function analyzeDeck(deck: CardInstance[]): {
  avgPower: string;
  maxPower: number;
  categoryCount: Record<string, number>;
  tierCount: Record<string, number>;
} {
  let totalPower = 0;
  let maxPower = 0;
  const categoryCount: Record<string, number> = {};
  const tierCount: Record<string, number> = { A: 0, B: 0, C: 0 };

  for (const card of deck) {
    const def = getCardById(card.defId);
    if (def) {
      totalPower += card.currentPower;
      maxPower = Math.max(maxPower, card.currentPower);
      categoryCount[def.category] = (categoryCount[def.category] ?? 0) + 1;
      tierCount[def.tier] = (tierCount[def.tier] ?? 0) + 1;
    }
  }

  return {
    avgPower: deck.length > 0 ? (totalPower / deck.length).toFixed(1) : '0',
    maxPower,
    categoryCount,
    tierCount,
  };
}

function getPhaseColor(phase: string): string {
  switch (phase) {
    case 'deckPhase': return '#3498db';
    case 'matchPhase': return '#e74c3c';
    case 'finals': return '#ffd700';
    case 'gameEnd': return '#2ecc71';
    default: return '#888';
  }
}

function getPhaseIcon(phase: string): string {
  switch (phase) {
    case 'deckPhase': return '🃏';
    case 'matchPhase': return '⚔️';
    case 'finals': return '🏆';
    case 'gameEnd': return '🎉';
    default: return '⏳';
  }
}

function getPhaseText(phase: string): string {
  switch (phase) {
    case 'deckPhase': return 'デッキ構築中';
    case 'matchPhase': return 'バトル中';
    case 'finals': return '決勝戦';
    case 'gameEnd': return 'ゲーム終了';
    default: return '待機中';
  }
}

function getCategoryIcon(category: string): string {
  const icons: Record<string, string> = {
    warrior: '⚔️', thief: '🗡️', mage: '🔮', machine: '⚙️', undead: '💀',
    dragon: '🐉', spirit: '✨', beast: '🐺', angel: '👼', demon: '😈', golem: '🗿',
  };
  return icons[category] ?? '?';
}

function getCategoryColor(category: string): string {
  const colors: Record<string, string> = {
    warrior: '#e74c3c', thief: '#9b59b6', mage: '#3498db', machine: '#95a5a6', undead: '#1abc9c',
    dragon: '#e67e22', spirit: '#00bcd4', beast: '#8d6e63', angel: '#ffd700', demon: '#8e44ad', golem: '#607d8b',
  };
  return colors[category] ?? '#666';
}

function getTierColor(tier: string): string {
  switch (tier) {
    case 'A': return '#2ecc71';
    case 'B': return '#3498db';
    case 'C': return '#9b59b6';
    default: return '#888';
  }
}

// 自動更新停止
export function stopAutoRefresh(): void {
  if (spectatorState.refreshInterval !== null) {
    clearInterval(spectatorState.refreshInterval);
    spectatorState.refreshInterval = null;
  }
}

// 状態リセット
export function resetSpectatorState(): void {
  spectatorState.selectedMatchIndex = 0;
  spectatorState.viewMode = 'ranking';
  spectatorState.selectedPlayerId = null;
  stopAutoRefresh();
}
